import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];
  #shopfloSyncTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener('click', this.#onDocumentClick, true);
    this.#syncCartTriggerAriaExpanded();
    this.#syncCartFromShopify();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener('click', this.#onDocumentClick, true);
    if (this.#shopfloSyncTimer !== null) window.clearTimeout(this.#shopfloSyncTimer);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Keep Horizon's cart badge in sync after cart changes made inside Shopflo.
   * Shopflo does not dispatch Horizon's cartLinesUpdate event, so the normal
   * cart-icon update path does not run for those interactions.
   */
  #syncCartFromShopify = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = Number(cart?.item_count ?? 0);

      await customElements.whenDefined('cart-icon');
      const cartIcon = this.querySelector('cart-icon');
      if (typeof cartIcon?.renderCartBubble === 'function') {
        await cartIcon.renderCartBubble(itemCount, false);
      }

      this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${itemCount}`;
    } catch (error) {
      console.warn('[header-actions] Unable to sync Shopflo cart count:', error);
    }
  };

  #scheduleShopfloCartSync = () => {
    if (this.#shopfloSyncTimer !== null) window.clearTimeout(this.#shopfloSyncTimer);

    this.#shopfloSyncTimer = window.setTimeout(() => {
      this.#shopfloSyncTimer = null;
      this.#syncCartFromShopify();

      // Shopflo cart mutations can finish after its UI click handler resolves.
      window.setTimeout(() => this.#syncCartFromShopify(), 700);
    }, 250);
  };

  #isShopfloElement = (node) => {
    if (!(node instanceof Element)) return false;

    if (node.id?.toLowerCase().includes('shopflo') || node.id?.toLowerCase().startsWith('flo-')) return true;

    return Array.from(node.classList || []).some((className) => {
      const name = className.toLowerCase();
      return name.includes('shopflo') || name.startsWith('flo-');
    });
  };

  #submitNativeCheckoutFallback = () => {
    const cartForm = document.getElementById('cart-form');
    if (!(cartForm instanceof HTMLFormElement)) {
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}checkout`);
      return;
    }

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Checkout';
    cartForm.appendChild(checkoutInput);
    cartForm.submit();
  };

  #onDocumentClick = (event) => {
    const path = event.composedPath();
    const isShopfloInteraction = path.some(this.#isShopfloElement);
    const clickable = path.find(
      (node) => node instanceof Element && node.matches('button, a, [role="button"]')
    );

    // Capture the Shopflo checkout button before its inline handler runs. This
    // prevents a ReferenceError if the async Shopflo bridge has not loaded and
    // keeps native Shopify checkout available as a safe fallback.
    if (clickable instanceof Element && clickable.id === 'checkout2') {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (typeof window.handleFloCheckoutBtn === 'function') {
        window.handleFloCheckoutBtn();
      } else {
        this.#submitNativeCheckoutFallback();
      }
      return;
    }

    if (isShopfloInteraction && clickable instanceof Element) {
      const label = (clickable.getAttribute('aria-label') || clickable.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      if (label === 'shop now') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const root = window.Shopify?.routes?.root || '/';
        window.location.assign(`${root}collections/shop`);
        return;
      }

      this.#scheduleShopfloCartSync();
      return;
    }

    if (clickable instanceof Element && clickable.matches('[data-testid="cart-drawer-trigger"], a.action__cart')) {
      this.#scheduleShopfloCartSync();
    }
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
